import type { CodeableConcept, Extension } from '@medplum/fhirtypes';
import { MEDICATION_CODE_SYSTEMS, MEDICATION_NAMES_EXTENSION } from './identifiers';
import { getMedicationRxNormCode, type MedicationListRecord } from './medicationRecords';
import { formulationDisplay, getFreshScriptSureFormulation, type ScriptSureFormulation } from './scriptsureFormulation';

/** One resolved ingredient of the product: the patient-friendly IN name plus the raw RxNorm
 * strength string (e.g. "12.5 MG" — not parsed into value/unit). */
export type MedicationIngredient = {
  name: string;
  strength: string;
};

/**
 * Structured names behind a medication's resolved display text, persisted as one complex
 * {@link MEDICATION_NAMES_EXTENSION} extension on `medicationCodeableConcept`. The resolved display
 * itself stays on the CodeableConcept's `text`; these are the unique elements a consumer can read
 * without re-deriving from RxNorm (e.g. "show the brand name only", or compose a per-surface
 * display from the ingredient parts).
 */
export type MedicationNames = {
  /** BN concept name for a branded (SBD) product, e.g. "Zestoretic". Absent on generics. */
  brandName?: string;
  /** Ingredient (IN) names joined with " / ", e.g. "hydrochlorothiazide / lisinopril" — the cheap
   * one-line read. Consumers composing their own format should prefer `ingredients`. */
  genericName?: string;
  /** Per-ingredient name + strength, in display order. Only present when every component fully
   * resolved — a partial ingredient list is never persisted. */
  ingredients?: MedicationIngredient[];
  /** The RxNorm product code the names were resolved from. Present whenever resolution succeeded
   * (even if neither name applied), so a mismatch with the current RxNorm coding marks the names
   * as stale rather than merely missing. */
  resolvedFromRxNorm?: string;
};

const SUB_URL = {
  brand: 'brand',
  generic: 'generic',
  ingredient: 'ingredient',
  name: 'name',
  strength: 'strength',
  rxnorm: 'rxnorm',
} as const;

/** Build the complex medication-names extension, or `undefined` when there is nothing to record —
 * so an unresolvable medication carries no extension rather than an empty one. */
export function buildMedicationNamesExtension(names: MedicationNames): Extension | undefined {
  const parts: Extension[] = [];
  if (names.brandName) parts.push({ url: SUB_URL.brand, valueString: names.brandName });
  if (names.genericName) parts.push({ url: SUB_URL.generic, valueString: names.genericName });
  for (const ingredient of names.ingredients ?? []) {
    parts.push({
      url: SUB_URL.ingredient,
      extension: [
        { url: SUB_URL.name, valueString: ingredient.name },
        { url: SUB_URL.strength, valueString: ingredient.strength },
      ],
    });
  }
  if (names.resolvedFromRxNorm) parts.push({ url: SUB_URL.rxnorm, valueCode: names.resolvedFromRxNorm });
  if (parts.length === 0) return undefined;
  return { url: MEDICATION_NAMES_EXTENSION, extension: parts };
}

/** Read the structured medication names off a `medicationCodeableConcept`. All fields are absent
 * when the extension was never written (pre-backfill records, uncoded meds). */
export function getMedicationNames(concept: CodeableConcept | undefined): MedicationNames {
  const ext = concept?.extension?.find((e) => e.url === MEDICATION_NAMES_EXTENSION);
  const part = (url: string): Extension | undefined => ext?.extension?.find((e) => e.url === url);
  const ingredients = (ext?.extension ?? [])
    .filter((e) => e.url === SUB_URL.ingredient)
    .flatMap((e) => {
      const name = e.extension?.find((p) => p.url === SUB_URL.name)?.valueString;
      const strength = e.extension?.find((p) => p.url === SUB_URL.strength)?.valueString;
      return name && strength ? [{ name, strength }] : [];
    });
  return {
    brandName: part(SUB_URL.brand)?.valueString,
    genericName: part(SUB_URL.generic)?.valueString,
    ingredients: ingredients.length > 0 ? ingredients : undefined,
    resolvedFromRxNorm: part(SUB_URL.rxnorm)?.valueCode,
  };
}

/** Names off the record, guarded by the staleness marker: names resolved from a different RxNorm
 * code than the record currently carries describe the wrong drug (coding changed; the hook heal is
 * async and can defer on a terminology failure) — treat them as absent rather than surface them. */
function getFreshMedicationNames(record: MedicationListRecord | undefined): MedicationNames {
  if (!record) return {};
  const names = getMedicationNames(record.medicationCodeableConcept);
  if (names.resolvedFromRxNorm !== getMedicationRxNormCode(record)) return {};
  return names;
}

/** Display casing for the unit tokens RxNorm writes in caps in `RXN_STRENGTH` values. The
 * vocabulary mirrors the RxNorm unit tokens observed in our data (see `NON_NAME_TOKENS` in
 * medicationCodingUtils.ts). Deliberately not exhaustive: tokens not listed (UNT, ACTUAT, MEQ,
 * CELLS, BAU, …) pass through unchanged — an unfamiliar-but-honest "100 UNT/mL" beats a mangled
 * "100 unt/ml". */
const STRENGTH_UNIT_DISPLAY: Record<string, string> = {
  MG: 'mg',
  MCG: 'mcg',
  NG: 'ng',
  G: 'g',
  GM: 'g',
  ML: 'mL',
  L: 'L',
  MMOL: 'mmol',
  HR: 'hr',
  DAY: 'day',
};

/** Recase a raw RxNorm strength for display ("12.5 MG" → "12.5 mg", "100 UNT/ML" → "100 UNT/mL"). */
function formatStrengthForDisplay(strength: string): string {
  return strength.replace(/[A-Za-z]+/g, (token) => STRENGTH_UNIT_DISPLAY[token.toUpperCase()] ?? token);
}

/** The two title lines joined back into one string, for contexts that can only carry a single line —
 * a Task description, a notification, a chat mention. `undefined` when there is nothing composable,
 * so callers keep their own fallback. */
export function joinMedicationTitleParts(parts: MedicationTitleParts | undefined): string | undefined {
  if (!parts) return undefined;
  return parts.subtitle ? `${parts.name} ${parts.subtitle}` : parts.name;
}

/** A medication title split into the two lines the care plan renders: the drug's name, and the
 * strength/form beneath it. `subtitle` is absent when there is nothing to put on the second line. */
export type MedicationTitleParts = {
  name: string;
  subtitle?: string;
};

/** Tidy vendor punctuation for display — ScriptSure writes "tablet,extended release 24 hr" with no
 * space after the comma. */
function normalizeSeparators(text: string): string {
  return text.replace(/,\s*/g, ', ').trim();
}

/** Whether a coding's `display` is a vendor product label rather than an RxNorm normalized name.
 * ScriptSure writes the product label ("Actoplus MET 15 mg-850 mg tablet"); RxNorm writes
 * " / "-joined ingredient lists and "[Brand]"-suffixed names ("hydrocortisone 10 MG/ML Topical
 * Lotion [Cortizone]"), which carry no form and would leak a trailing bracket into the subtitle.
 * Bare term-type codes ("SBD") and anything without a space are not labels either. Mirrors
 * `isRxNormStyleName` in the server's medicationStatementBuilder. */
function isVendorProductLabel(display: string | undefined): display is string {
  if (!display) return false;
  const trimmed = display.trim();
  if (!trimmed.includes(' ')) return false;
  return !trimmed.includes(' / ') && !/\[[^\]]+\]\s*$/.test(trimmed);
}

/** Split a Liza-composed branded display, which the resolver writes as `[Brand] <ingredient parts>`
 * (see `resolveMedicationDisplay`). The bracket is a real delimiter, so this is exact. */
function splitBracketedBrand(text: string): MedicationTitleParts | undefined {
  const match = /^\[([^\]]+)\]\s*(.*)$/.exec(text.trim());
  if (!match) return undefined;
  const [, brand, rest] = match;
  if (!brand) return undefined;
  return { name: brand, ...(rest ? { subtitle: normalizeSeparators(rest) } : {}) };
}

/** The names a product label may lead with, most specific first: the brand, the generic as
 * resolved, and the distinct ingredients joined the two ways vendor labels join them
 * ("dextroamphetamine-amphetamine", "lisinopril/hydrochlorothiazide"). The ingredients are in the
 * label order the resolver stored them in, with duplicate salts collapsed. */
function knownNamePrefixes(names: MedicationNames): string[] {
  const ingredients = [...new Set((names.ingredients ?? []).map((i) => i.name))];
  const joined = ingredients.length > 1 ? [ingredients.join('-'), ingredients.join('/')] : [];
  return [names.brandName, names.genericName, ...joined].filter((p): p is string => Boolean(p));
}

/** Split a vendor display that leads with the drug's name — ScriptSure writes the whole product as
 * one string ("metformin ER 500 mg tablet,extended release 24 hr"), so the name we already hold is
 * the prefix and everything after it is the strength/form. Only splits on a word boundary, so
 * "metformin" can't match inside "metforminXR". */
function splitKnownPrefix(text: string, prefix: string | undefined): MedicationTitleParts | undefined {
  if (!prefix) return undefined;
  const trimmed = text.trim();
  if (!trimmed.toLowerCase().startsWith(prefix.toLowerCase())) return undefined;
  const rest = trimmed.slice(prefix.length);
  if (rest && !/^[\s,]/.test(rest)) return undefined;
  const subtitle = normalizeSeparators(rest.replace(/^[\s,]+/, ''));
  return { name: trimmed.slice(0, prefix.length), ...(subtitle ? { subtitle } : {}) };
}

/** Compose the two lines from the structured names alone, for records whose `text` we can't split:
 * the drug's name over its strengths. */
function composeParts(names: MedicationNames): MedicationTitleParts | undefined {
  const ingredients = names.ingredients ?? [];
  const strengths = ingredients.map((i) => formatStrengthForDisplay(i.strength)).join(' / ');

  if (names.brandName) {
    return { name: names.brandName, ...(strengths ? { subtitle: strengths } : {}) };
  }
  if (ingredients.length === 0) return undefined;
  return { name: ingredients.map((i) => i.name).join(' / '), ...(strengths ? { subtitle: strengths } : {}) };
}

/**
 * The two lines straight from a recorded formulation, when it carries the drug's name separately:
 * the drug over the vendor's strength/form label. When the label already leads with the drug name
 * ("metformin 500 MG Oral Tablet" under "Metformin") the split is made after it; a label that
 * carries the drug name elsewhere ("Salonpas (lidocaine) 4 % patch" under "lidocaine") is one line,
 * whole, so nothing is rendered twice. `undefined` only when there is no drug name to lead with.
 */
function formulationParts(formulation: ScriptSureFormulation): MedicationTitleParts | undefined {
  if (!formulation.drugName) return undefined;
  const byPrefix = splitKnownPrefix(formulation.formulation, formulation.drugName);
  if (byPrefix) return byPrefix;
  if (formulation.formulation.toLowerCase().includes(formulation.drugName.toLowerCase())) {
    return { name: normalizeSeparators(formulation.formulation) };
  }
  return { name: formulation.drugName, subtitle: normalizeSeparators(formulation.formulation) };
}

/**
 * Split a medication record's title into the name and the strength/form line beneath it, or
 * `undefined` when there is nothing composable (uncoded/unresolved/stale records — callers fall back
 * to the task-description snapshot).
 *
 * A record that carries the formulation the prescriber picked (the `scriptsure-formulation`
 * extension, fresh against the current RxNorm code) is read, not parsed: the drug's name over the
 * vendor's label, exactly as the picker offered it. Everything else is recovered from strings, in
 * order:
 *
 * 1. The recorded formulation's composed product name, or the vendor label on the coding, or `text`
 *    — each tried as: `[Brand] rest` split on the bracket, then a string leading with a name we hold,
 *    split after that name.
 *    The names tried as a prefix are the brand, the generic, and the distinct ingredients joined the
 *    way vendor labels join them ("dextroamphetamine-amphetamine", "lisinopril/hydrochlorothiazide").
 * 2. A fresh recorded formulation that no split could open — shown whole on one line. It is the
 *    product the prescriber picked, so it always beats what follows.
 * 3. A vendor label on the coding that no split could open — also whole on one line. It names the
 *    product; the composed names below only name its parts.
 * 4. Neither — composed from the structured names ("lisinopril / hydrochlorothiazide" over
 *    "10 mg / 12.5 mg"), which is where a label-ordered multi-ingredient text with no vendor label
 *    lands, since the persisted `genericName` is alphabetical and won't prefix-match it.
 *
 * A record with no usable names yields a single line (no `subtitle`) rather than an empty one.
 */
export function getMedicationTitleParts(record: MedicationListRecord | undefined): MedicationTitleParts | undefined {
  const formulation = getFreshScriptSureFormulation(record);
  if (formulation) {
    const parts = formulationParts(formulation);
    if (parts) return parts;
  }

  const names = getFreshMedicationNames(record);
  const concept = record?.medicationCodeableConcept;
  const text = concept?.text?.trim();
  const rxNormDisplay = concept?.coding?.find((c) => c.system === MEDICATION_CODE_SYSTEMS.RXNORM)?.display;

  // A Liza-authored plan composes `text` from the ingredient parts ("metformin 500 MG"), while the
  // order ScriptSure writes back from it carries the full product name. Preferring the vendor label
  // on the coding keeps a medication reading the same before and after it is prescribed. A recorded
  // formulation without a separate drug name (an RxNorm-catalog row) contributes its product name
  // as the first candidate, ahead of both.
  const candidates = [
    formulation ? formulationDisplay(formulation.drugName, formulation.formulation) : undefined,
    isVendorProductLabel(rxNormDisplay) ? rxNormDisplay.trim() : undefined,
    text,
  ];
  for (const candidate of candidates) {
    if (!candidate) continue;
    const bracketed = splitBracketedBrand(candidate);
    if (bracketed) return bracketed;

    for (const prefix of knownNamePrefixes(names)) {
      const byName = splitKnownPrefix(candidate, prefix);
      if (byName) return byName;
    }
  }

  // A fresh recorded formulation that no split could open still beats the composed names and the raw
  // text: it is the product the prescriber picked, whole, on one line.
  if (formulation)
    return { name: normalizeSeparators(formulationDisplay(formulation.drugName, formulation.formulation)) };

  // Likewise a vendor label nothing could split: it names the product ("dextroamphetamine-amphetamine
  // ER 10 mg 24hr capsule,extend release"), where the composed names would show four salts and four
  // strengths. Whole beats composed.
  if (isVendorProductLabel(rxNormDisplay)) return { name: normalizeSeparators(rxNormDisplay) };

  return composeParts(names) ?? (text ? { name: text } : undefined);
}
